// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Session state for administration applications.
 *
 * The browser holds no credential. The BFF owns an `HttpOnly`, `Secure`,
 * `SameSite` cookie and this provider only reads the derived, non-secret view
 * of it (`SEC2-019`, `07-SECURITY.md` §10.3 and §15.3). Nothing here writes to
 * `localStorage` or `sessionStorage`; the workspace lint configuration rejects
 * such access outright.
 *
 * Two behaviours are required by `docs/10-TESTING.md` §30 and implemented here:
 *
 * - **session expiry** — when the BFF reports the session is no longer
 *   authenticated, cached data is discarded rather than left on screen; and
 * - **cross-tenant state clearing** — when the reported tenant changes, the
 *   entire query cache is cleared before the new tenant's data is fetched, so
 *   no previous tenant's rows can survive in a cache
 *   (`SEC2-016`, `DAT-006`).
 */

import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from 'react';

import { queryKeys, tenantScope } from '../api-clients/queryKeys';
import { useApiClients } from '../api-clients/ApiClientProvider';
import { ApiProblemError } from '../api-clients/problem';
import type { SessionView } from '../api-clients/views';

/** Session state exposed to components. */
export interface SessionState {
  /** Session as reported by the BFF, or `undefined` before the first result. */
  readonly session: SessionView | undefined;
  /** Tenant-scoped cache prefix derived from the session. */
  readonly scope: string;
  /** Whether the first session query is still in flight. */
  readonly isLoading: boolean;
  /** Failure of the session query, when it failed. */
  readonly error: ApiProblemError | undefined;
  /** Re-read the session. */
  readonly refresh: () => Promise<void>;
  /** End the session and clear every cached tenant-scoped result. */
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

/** How often the session view is revalidated, in milliseconds. */
export const SESSION_REFETCH_INTERVAL_MS = 60_000;

/** Props for {@link SessionProvider}. */
export interface SessionProviderProps {
  readonly children: ReactNode;
}

function toApiProblemError(error: unknown): ApiProblemError | undefined {
  if (error === null || error === undefined) {
    return undefined;
  }
  return error instanceof ApiProblemError
    ? error
    : new ApiProblemError('The session could not be read.', {
        kind: 'server',
        status: 0,
        cause: error,
      });
}

/** Load and publish the current session. */
export function SessionProvider({ children }: SessionProviderProps): ReactNode {
  const clients = useApiClients();
  const queryClient = useQueryClient();
  const previousScope = useRef<string | null>(null);

  const query: UseQueryResult<SessionView, unknown> = useQuery({
    queryKey: queryKeys.session(),
    queryFn: ({ signal }) => clients.session.getSession(signal),
    refetchInterval: SESSION_REFETCH_INTERVAL_MS,
    // An unauthenticated or denied response is an answer, not a transient
    // fault: retrying it would only add load (`RUN-010`, bounded retries).
    retry: (failureCount, error) => {
      if (error instanceof ApiProblemError && !error.retryable) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const session = query.data;
  const scope = tenantScope(session?.tenant_id);

  useEffect(() => {
    const previous = previousScope.current;
    previousScope.current = scope;
    if (previous === null || previous === scope) {
      return;
    }
    // The tenant changed (including sign-out and expiry): discard every result
    // cached under the *previous* scope so it cannot be served to the new one.
    //
    // Only the previous scope is removed. Removing every non-session key would
    // also cancel queries the new scope has already started, leaving them
    // permanently pending with no observer to restart them.
    queryClient.removeQueries({
      predicate: (cached) => cached.queryKey[0] === previous,
    });
  }, [queryClient, scope]);

  const refresh = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session() });
  }, [queryClient]);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await clients.session.signOut();
    } finally {
      // Clearing the cache discards every tenant-scoped result, which is the
      // point. It does not, by itself, produce a new session answer: the
      // observer is left holding the last one it saw. Without the refetch the
      // shell stays on screen after signing out — the user believes they have
      // left and the previous view is still rendered.
      queryClient.clear();
      await query.refetch();
    }
  }, [clients, query, queryClient]);

  const value: SessionState = {
    session,
    scope,
    isLoading: query.isPending,
    error: toApiProblemError(query.error),
    refresh,
    signOut,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Read the current session state.
 *
 * @returns The state published by the nearest {@link SessionProvider}.
 * @throws Error If no provider is mounted, which is a wiring defect.
 */
export function useSession(): SessionState {
  const state = useContext(SessionContext);
  if (state === null) {
    throw new Error('useSession requires a <SessionProvider> ancestor.');
  }
  return state;
}
